import {ApplicantDemographics, matchApplicantDemographicsByType} from 'ApplicationEntry/models/ApplicantDemographics';
import {AWDStatus, matchAWDStatusByType} from 'ApplicationEntry/models/AWDStatus/AWDStatus';
import {BillingAgent, matchBillingAgentByType} from 'ApplicationEntry/models/BillingAgent';
import {BillingModes} from 'ApplicationEntry/models/BillingModes';
import {CaseDetails, matchCaseDetailsByType} from 'ApplicationEntry/models/CaseDetails';
import {ContingentBeneficiary} from 'ApplicationEntry/models/ContingentBeneficiary';
import {ContingentBeneficiaryList} from 'ApplicationEntry/models/ContingentBeneficiaryList';
import {DependentLists} from 'ApplicationEntry/models/DependentLists';
import {Dependents} from 'ApplicationEntry/models/Dependents';
import * as G from 'ApplicationEntry/models/GroupApplication';
import {HippaTrustees} from 'ApplicationEntry/models/HippaTrustees';
import * as NY from 'ApplicationEntry/models/NewYorkApplication';
import * as O from 'ApplicationEntry/models/OrdinaryApplication';
import {PlanCode} from 'ApplicationEntry/models/PlanCode';
import {PlanCodes} from 'ApplicationEntry/models/PlanCodes';
import {Premium} from 'ApplicationEntry/models/Premium';
import {PrimaryBeneficiary} from 'ApplicationEntry/models/PrimaryBeneficiary';
import {PrimaryBeneficiaryList} from 'ApplicationEntry/models/PrimaryBeneficiaryList';
import * as A from 'ApplicationEntry/models/ProductsRiders/Accident/Accident';
import * as ANY from 'ApplicationEntry/models/ProductsRiders/AccidentNewYork/AccidentNewYork';
import {Cancer, matchCancerByType} from 'ApplicationEntry/models/ProductsRiders/Cancer/Cancer';
import {
  CriticalIllness,
  matchCriticalIllnessByType,
} from 'ApplicationEntry/models/ProductsRiders/CriticalIllness/CriticalIllness';
import {
  CriticalIllnessNY,
  matchCriticalIllnessNYByType,
} from 'ApplicationEntry/models/ProductsRiders/CriticalIllnessNY/CriticalIllnessNY';
import {Disability, matchDisabilityByType} from 'ApplicationEntry/models/ProductsRiders/Disability/Disability';
import {GLTERM, matchGLTERMByType} from 'ApplicationEntry/models/ProductsRiders/GLTERM/GLTERM';
import * as HS from 'ApplicationEntry/models/ProductsRiders/HeartStroke/HeartStroke';
import {ICRTN, matchICRTNByType} from 'ApplicationEntry/models/ProductsRiders/ICRTN/ICRTN';
import {IWL, matchIWLByType} from 'ApplicationEntry/models/ProductsRiders/IWL/IWL';
import * as P from 'ApplicationEntry/models/ProductsRiders/PinCare/PinCare';
import {matchShopByType, Shop} from 'ApplicationEntry/models/ProductsRiders/Shop/Shop';
import {SecondaryAddressee} from 'ApplicationEntry/models/SecondaryAddressee';
import {UWQuestions} from 'ApplicationEntry/models/UnderwritingQuestions';
import {identity} from 'fp-ts/lib/function';
import {AdditionalProperties} from './AdditionalProperties';
import {AgentInformation} from './AgentInformation';
import {AgentList} from './AgentList';
import {Messages} from './Messages';
import {OwnerInfo} from './OwnerInfo';
import {PolicyRecord} from './PolicyRecord';
import {AIP, matchAIPByType} from './ProductsRiders/AIP/AIP';
import {matchULByType, UniversalLife} from './ProductsRiders/UniversalLife/UniversalLife';
import {matchVerifyCriticalInformationByType, VerifyCriticalInformation} from './VerifyCriticalInformation';

type Undetermined = {key: 'UndeterminedApplication'};
type Loading = {key: 'LoadingApplication'};

export const UndeterminedApplication: Undetermined = {key: 'UndeterminedApplication'};
export const LoadingApplication: Loading = {key: 'LoadingApplication'};

export type Application = Undetermined | Loading | InvalidApplication | ValidApplication;

export type InvalidApplication =
  | G.InvalidGroupApplication
  | O.InvalidOrdinaryApplication
  | NY.InvalidNewYorkApplication;

export type ValidApplication = G.ValidGroupApplication | O.ValidOrdinaryApplication | NY.ValidNewYorkApplication;

type ExhaustiveApplicationOptions<TReturn> = {
  Invalid: (a: InvalidApplication) => TReturn;
  Valid: (a: ValidApplication) => TReturn;
  Undetermined: (a: Undetermined | Loading) => TReturn;
};

type PartialApplicationOptions<TReturn> = {
  Invalid?: (a: InvalidApplication) => TReturn;
  Valid?: (a: ValidApplication) => TReturn;
  Undetermined?: (a: Undetermined | Loading) => TReturn;
  _: (a: Application) => TReturn;
};
const isExhaustive = <TReturn>(
  options: ApplicationOptions<TReturn>
): options is ExhaustiveApplicationOptions<TReturn> => !!options.Invalid && !!options.Valid;

type ApplicationOptions<TReturn> = ExhaustiveApplicationOptions<TReturn> | PartialApplicationOptions<TReturn>;

export const matchApplication = <TReturn>(application: Application, options: ApplicationOptions<TReturn>): TReturn => {
  switch (application.key) {
    case 'InvalidGroupApplication':
    case 'InvalidOrdinaryApplication':
    case 'InvalidNewYorkApplication':
      return isExhaustive(options)
        ? options.Invalid(application)
        : options.Invalid
        ? options.Invalid(application)
        : options._(application);
    case 'ValidGroupApplication':
    case 'ValidNewYorkApplication':
    case 'ValidOrdinaryApplication':
      return isExhaustive(options)
        ? options.Valid(application)
        : options.Valid
        ? options.Valid(application)
        : options._(application);
    case 'UndeterminedApplication':
    case 'LoadingApplication':
      return isExhaustive(options)
        ? options.Undetermined(application)
        : options.Undetermined
        ? options.Undetermined(application)
        : options._(application);
  }
};

type ExhaustiveApplicationOptionsByType<TReturn> = {
  Group: (group: G.GroupApplication) => TReturn;
  NewYork: (newYork: NY.NewYorkApplication) => TReturn;
  Ordinary: (ordinary: O.OrdinaryApplication) => TReturn;
  Undetermined: (undetermined: Undetermined | Loading) => TReturn;
};

type PartialApplicationOptionsByType<TReturn> = {
  Group?: (group: G.GroupApplication) => TReturn;
  NewYork?: (newYork: NY.NewYorkApplication) => TReturn;
  Ordinary?: (ordinary: O.OrdinaryApplication) => TReturn;
  Undetermined?: (undetermined: Undetermined | Loading) => TReturn;
  _: (app: Application) => TReturn;
};

const isExhaustiveByType = <TReturn>(
  options: ApplicationByTypeOptions<TReturn>
): options is ExhaustiveApplicationOptionsByType<TReturn> => !!options.Group && !!options.NewYork && !!options.Ordinary;

type ApplicationByTypeOptions<TReturn> =
  | PartialApplicationOptionsByType<TReturn>
  | ExhaustiveApplicationOptionsByType<TReturn>;

export const matchApplicationByType = <TReturn>(options: ApplicationByTypeOptions<TReturn>) => (
  application: Application
): TReturn => {
  switch (application.key) {
    case 'InvalidGroupApplication':
    case 'ValidGroupApplication':
      return isExhaustiveByType(options)
        ? options.Group(application)
        : options.Group
        ? options.Group(application)
        : options._(application);
    case 'InvalidNewYorkApplication':
    case 'ValidNewYorkApplication':
      return isExhaustiveByType(options)
        ? options.NewYork(application)
        : options.NewYork
        ? options.NewYork(application)
        : options._(application);
    case 'InvalidOrdinaryApplication':
    case 'ValidOrdinaryApplication':
      return isExhaustiveByType(options)
        ? options.Ordinary(application)
        : options.Ordinary
        ? options.Ordinary(application)
        : options._(application);
    case 'UndeterminedApplication':
    case 'LoadingApplication':
      return isExhaustiveByType(options)
        ? options.Undetermined(application)
        : options.Undetermined
        ? options.Undetermined(application)
        : options._(application);
  }
};

export const changeCaseDetails = (caseDetails: CaseDetails) =>
  matchApplicationByType<Application>({
    Group: groupApp =>
      matchCaseDetailsByType({
        Group: groupCaseDetails => G.ValidGroupApplication.createApp({...groupApp, caseDetails: groupCaseDetails}),
        _: () => groupApp,
      })(caseDetails),
    NewYork: nyApp =>
      matchCaseDetailsByType({
        NewYork: newYorkCaseDetails =>
          NY.ValidNewYorkApplication.createApp({...nyApp, caseDetails: newYorkCaseDetails}),
        _: () => nyApp,
      })(caseDetails),
    Ordinary: ordinaryApp =>
      matchCaseDetailsByType({
        Ordinary: ordinaryCaseDetails =>
          O.ValidOrdinaryApplication.createApp({...ordinaryApp, caseDetails: ordinaryCaseDetails}),
        _: () => ordinaryApp,
      })(caseDetails),
    Undetermined: identity,
  });

export const changeApplicantDemographics = (applicantDemographics: ApplicantDemographics) =>
  matchApplicationByType<Application>({
    Group: groupApp =>
      matchApplicantDemographicsByType<Application>({
        Group: groupAppDemographics =>
          G.ValidGroupApplication.createApp({...groupApp, applicantDemographics: groupAppDemographics}),
        _: () => groupApp,
      })(applicantDemographics),
    NewYork: newYorkApp =>
      matchApplicantDemographicsByType<Application>({
        NewYork: nyAppDemographics =>
          NY.ValidNewYorkApplication.createApp({...newYorkApp, applicantDemographics: nyAppDemographics}),
        _: () => newYorkApp,
      })(applicantDemographics),
    Ordinary: ordinaryApp =>
      matchApplicantDemographicsByType<Application>({
        Ordinary: ordinaryDemographics =>
          O.ValidOrdinaryApplication.createApp({...ordinaryApp, applicantDemographics: ordinaryDemographics}),
        _: () => ordinaryApp,
      })(applicantDemographics),
    Undetermined: identity,
  });

export const changeOwnerInfo = (ownerInfo: OwnerInfo) =>
  matchApplicationByType<Application>({
    Group: groupApp => G.ValidGroupApplication.createApp({...groupApp, ownerInfo}),
    NewYork: nyApp => NY.ValidNewYorkApplication.createApp({...nyApp, ownerInfo}),
    Ordinary: ordinaryApp => O.ValidOrdinaryApplication.createApp({...ordinaryApp, ownerInfo}),
    Undetermined: identity,
  });

export const changeSecondaryAddressee = (secondaryAddressee: SecondaryAddressee) =>
  matchApplicationByType<Application>({
    Group: groupApp => G.ValidGroupApplication.createApp({...groupApp, secondaryAddressee}),
    NewYork: nyApp => NY.ValidNewYorkApplication.createApp({...nyApp, secondaryAddressee}),
    Ordinary: ordinaryApp => O.ValidOrdinaryApplication.createApp({...ordinaryApp, secondaryAddressee}),
    Undetermined: identity,
  });

export const changePrimaryBeneficiaryList = (primaryBeneficiaryList: PrimaryBeneficiaryList) =>
  matchApplicationByType<Application>({
    Group: groupApp => G.ValidGroupApplication.createApp({...groupApp, primaryBeneficiaryList}),
    NewYork: nyApp => NY.ValidNewYorkApplication.createApp({...nyApp, primaryBeneficiaryList}),
    Ordinary: ordinaryApp => O.ValidOrdinaryApplication.createApp({...ordinaryApp, primaryBeneficiaryList}),
    Undetermined: identity,
  });

export const changePrimaryBeneficiary = (primaryBeneficiary: PrimaryBeneficiary) =>
  matchApplicationByType<Application>({
    Group: groupApp => G.ValidGroupApplication.createApp({...groupApp, primaryBeneficiary}),
    NewYork: nyApp => NY.ValidNewYorkApplication.createApp({...nyApp, primaryBeneficiary}),
    Ordinary: ordinaryApp => O.ValidOrdinaryApplication.createApp({...ordinaryApp, primaryBeneficiary}),
    Undetermined: identity,
  });

export const changeContingentBeneficiary = (contingentBeneficiary: ContingentBeneficiary) =>
  matchApplicationByType<Application>({
    Group: groupApp => G.ValidGroupApplication.createApp({...groupApp, contingentBeneficiary}),
    NewYork: nyApp => NY.ValidNewYorkApplication.createApp({...nyApp, contingentBeneficiary}),
    Ordinary: ordinaryApp => O.ValidOrdinaryApplication.createApp({...ordinaryApp, contingentBeneficiary}),
    Undetermined: identity,
  });

export const changeContingentBeneficiaryList = (contingentBeneficiaryList: ContingentBeneficiaryList) =>
  matchApplicationByType<Application>({
    Group: groupApp => G.ValidGroupApplication.createApp({...groupApp, contingentBeneficiaryList}),
    NewYork: nyApp => NY.ValidNewYorkApplication.createApp({...nyApp, contingentBeneficiaryList}),
    Ordinary: ordinaryApp => O.ValidOrdinaryApplication.createApp({...ordinaryApp, contingentBeneficiaryList}),
    Undetermined: identity,
  });

export const changeCriticalIllness = (criticalIllness: CriticalIllness) =>
  matchApplicationByType<Application>({
    Group: groupApp =>
      matchCriticalIllnessByType<Application>({
        Group: groupCriticalIllness =>
          G.ValidGroupApplication.createApp({...groupApp, criticalIllness: groupCriticalIllness}),
        _: () => groupApp,
      })(criticalIllness),
    NewYork: newYorkApp =>
      matchCriticalIllnessByType<Application>({
        NewYork: nyCriticalIllness =>
          NY.ValidNewYorkApplication.createApp({...newYorkApp, criticalIllness: nyCriticalIllness}),
        _: () => newYorkApp,
      })(criticalIllness),
    Ordinary: ordinaryApp =>
      matchCriticalIllnessByType<Application>({
        Ordinary: ordCriticalIllness =>
          O.ValidOrdinaryApplication.createApp({...ordinaryApp, criticalIllness: ordCriticalIllness}),
        _: () => ordinaryApp,
      })(criticalIllness),
    Undetermined: identity,
  });

export const changeICRTN = (icrtn: ICRTN) =>
  matchApplicationByType<Application>({
    Group: groupApp =>
      matchICRTNByType<Application>({
        Group: groupICRTN => G.ValidGroupApplication.createApp({...groupApp, icrtn: groupICRTN}),
        _: () => groupApp,
      })(icrtn),
    NewYork: newYorkApp =>
      matchICRTNByType<Application>({
        NewYork: nyICRTN => NY.ValidNewYorkApplication.createApp({...newYorkApp, icrtn: nyICRTN}),
        _: () => newYorkApp,
      })(icrtn),
    Ordinary: ordinaryApp =>
      matchICRTNByType<Application>({
        Ordinary: ordinaryICRTN => O.ValidOrdinaryApplication.createApp({...ordinaryApp, icrtn: ordinaryICRTN}),
        _: () => ordinaryApp,
      })(icrtn),
    Undetermined: identity,
  });

export const changeCancer = (cancer: Cancer) =>
  matchApplicationByType<Application>({
    Group: groupApp =>
      matchCancerByType<Application>({
        Group: groupCancer => G.ValidGroupApplication.createApp({...groupApp, cancer: groupCancer}),
        _: () => groupApp,
      })(cancer),
    NewYork: newYorkApp =>
      matchCancerByType<Application>({
        NewYork: newYorkCancer => NY.ValidNewYorkApplication.createApp({...newYorkApp, cancer: newYorkCancer}),
        _: () => newYorkApp,
      })(cancer),
    Ordinary: ordinaryApp =>
      matchCancerByType<Application>({
        Ordinary: ordinaryCancer => O.ValidOrdinaryApplication.createApp({...ordinaryApp, cancer: ordinaryCancer}),
        _: () => ordinaryApp,
      })(cancer),
    Undetermined: identity,
  });

export const changeDependents = (dependents: Dependents) =>
  matchApplicationByType<Application>({
    Group: groupApp => G.ValidGroupApplication.createApp({...groupApp, dependents}),
    NewYork: nyApp => NY.ValidNewYorkApplication.createApp({...nyApp, dependents}),
    Ordinary: ordinaryApp => O.ValidOrdinaryApplication.createApp({...ordinaryApp, dependents}),
    Undetermined: identity,
  });

export const changeDependentList = (dependentLists: DependentLists) =>
  matchApplicationByType<Application>({
    Group: groupApp => G.ValidGroupApplication.createApp({...groupApp, dependentLists}),
    NewYork: nyApp => NY.ValidNewYorkApplication.createApp({...nyApp, dependentLists}),
    Ordinary: ordinaryApp => O.ValidOrdinaryApplication.createApp({...ordinaryApp, dependentLists}),
    Undetermined: identity,
  });

export const changePolicyRecord = (policyRecord: PolicyRecord) =>
  matchApplicationByType<Application>({
    Group: groupApp => G.ValidGroupApplication.createApp({...groupApp, policyRecord}),
    NewYork: nyApp => NY.ValidNewYorkApplication.createApp({...nyApp, policyRecord}),
    Ordinary: ordinaryApp => O.ValidOrdinaryApplication.createApp({...ordinaryApp, policyRecord}),
    Undetermined: identity,
  });

export const changeBillingAgent = (billingAgent: BillingAgent) =>
  matchApplicationByType<Application>({
    Group: groupApp =>
      matchBillingAgentByType({
        Group: groupBillingAgent => G.ValidGroupApplication.createApp({...groupApp, billingAgent: groupBillingAgent}),
        _: () => groupApp,
      })(billingAgent),
    NewYork: nyApp =>
      matchBillingAgentByType({
        NewYork: newYorkBillingAgent =>
          NY.ValidNewYorkApplication.createApp({...nyApp, billingAgent: newYorkBillingAgent}),
        _: () => nyApp,
      })(billingAgent),
    Ordinary: ordinaryApp =>
      matchBillingAgentByType({
        Ordinary: ordinaryBillingAgent =>
          O.ValidOrdinaryApplication.createApp({...ordinaryApp, billingAgent: ordinaryBillingAgent}),
        _: () => ordinaryApp,
      })(billingAgent),
    Undetermined: identity,
  });

export const changeAgent = (agentInformation: AgentInformation) =>
  matchApplicationByType<Application>({
    Group: groupApp => G.ValidGroupApplication.createApp({...groupApp, agentInformation}),
    NewYork: nyApp => NY.ValidNewYorkApplication.createApp({...nyApp, agentInformation}),
    Ordinary: ordinaryApp => O.ValidOrdinaryApplication.createApp({...ordinaryApp, agentInformation}),
    Undetermined: identity,
  });

export const changeAgentList = (agentList: AgentList) =>
  matchApplicationByType<Application>({
    Group: groupApp => G.ValidGroupApplication.createApp({...groupApp, agentList}),
    NewYork: nyApp => NY.ValidNewYorkApplication.createApp({...nyApp, agentList}),
    Ordinary: ordinaryApp => O.ValidOrdinaryApplication.createApp({...ordinaryApp, agentList}),
    Undetermined: identity,
  });

export const changeMessage = (messages: Messages) =>
  matchApplicationByType<Application>({
    Group: groupApp => G.ValidGroupApplication.createApp({...groupApp, messages}),
    NewYork: nyApp => NY.ValidNewYorkApplication.createApp({...nyApp, messages}),
    Ordinary: ordinaryApp => O.ValidOrdinaryApplication.createApp({...ordinaryApp, messages}),
    Undetermined: identity,
  });

export const changeBillingModes = (billingModes: BillingModes) =>
  matchApplicationByType<Application>({
    Group: groupApp => G.ValidGroupApplication.createApp({...groupApp, billingModes}),
    NewYork: nyApp => NY.ValidNewYorkApplication.createApp({...nyApp, billingModes}),
    Ordinary: ordinaryApp => O.ValidOrdinaryApplication.createApp({...ordinaryApp, billingModes}),
    Undetermined: identity,
  });

export const changeAWDStatus = (awdStatus: AWDStatus) =>
  matchApplicationByType<Application>({
    Group: groupApp =>
      matchAWDStatusByType({
        Group: groupAWDStatus => G.ValidGroupApplication.createApp({...groupApp, awdStatus: groupAWDStatus}),
        _: () => groupApp,
      })(awdStatus),
    NewYork: nyApp =>
      matchAWDStatusByType({
        NewYork: newYorkAWDStatus => NY.ValidNewYorkApplication.createApp({...nyApp, awdStatus: newYorkAWDStatus}),
        _: () => nyApp,
      })(awdStatus),
    Ordinary: ordinaryApp =>
      matchAWDStatusByType({
        Ordinary: ordinaryAWDStatus =>
          O.ValidOrdinaryApplication.createApp({...ordinaryApp, awdStatus: ordinaryAWDStatus}),
        _: () => ordinaryApp,
      })(awdStatus),
    Undetermined: identity,
  });

export const changeVerifyCriticalInformation = (verifyCriticalInformation: VerifyCriticalInformation) =>
  matchApplicationByType<Application>({
    Group: groupApp =>
      matchVerifyCriticalInformationByType({
        Group: verifyCriticalInformation => G.ValidGroupApplication.createApp({...groupApp, verifyCriticalInformation}),
        _: () => groupApp,
      })(verifyCriticalInformation),
    NewYork: nyApp =>
      matchVerifyCriticalInformationByType({
        NewYork: verifyCriticalInformation =>
          NY.ValidNewYorkApplication.createApp({...nyApp, verifyCriticalInformation}),
        _: () => nyApp,
      })(verifyCriticalInformation),
    Ordinary: ordinaryApp =>
      matchVerifyCriticalInformationByType({
        Ordinary: verifyCriticalInformation =>
          O.ValidOrdinaryApplication.createApp({...ordinaryApp, verifyCriticalInformation}),
        _: () => ordinaryApp,
      })(verifyCriticalInformation),
    Undetermined: identity,
  });

export const changeDisability = (disability: Disability) =>
  matchApplicationByType<Application>({
    Group: groupApp =>
      matchDisabilityByType({
        Group: groupDisability => G.ValidGroupApplication.createApp({...groupApp, disability: groupDisability}),
        _: () => groupApp,
      })(disability),
    NewYork: nyApp =>
      matchDisabilityByType({
        NewYork: newYorkDisability => NY.ValidNewYorkApplication.createApp({...nyApp, disability: newYorkDisability}),
        _: () => nyApp,
      })(disability),
    Ordinary: ordinaryApp =>
      matchDisabilityByType({
        Ordinary: ordinaryDisability =>
          O.ValidOrdinaryApplication.createApp({...ordinaryApp, disability: ordinaryDisability}),
        _: () => ordinaryApp,
      })(disability),
    Undetermined: identity,
  });

export const changeShop = (shop: Shop) =>
  matchApplicationByType<Application>({
    Group: groupApp =>
      matchShopByType({
        Group: groupShop => G.ValidGroupApplication.createApp({...groupApp, shop: groupShop}),
        _: () => groupApp,
      })(shop),
    NewYork: nyApp =>
      matchShopByType({
        NewYork: newYorkShop => NY.ValidNewYorkApplication.createApp({...nyApp, shop: newYorkShop}),
        _: () => nyApp,
      })(shop),
    Ordinary: ordinaryApp =>
      matchShopByType({
        Ordinary: ordinaryShop => O.ValidOrdinaryApplication.createApp({...ordinaryApp, shop: ordinaryShop}),
        _: () => ordinaryApp,
      })(shop),
    Undetermined: identity,
  });

export const changeIWL = (iwl: IWL) =>
  matchApplicationByType<Application>({
    Group: groupApp =>
      matchIWLByType({
        Group: groupIWL => G.ValidGroupApplication.createApp({...groupApp, iwl: groupIWL}),
        _: () => groupApp,
      })(iwl),
    NewYork: nyApp =>
      matchIWLByType({
        NewYork: newYorkIWL => NY.ValidNewYorkApplication.createApp({...nyApp, iwl: newYorkIWL}),
        _: () => nyApp,
      })(iwl),
    Ordinary: ordinaryApp =>
      matchIWLByType({
        Ordinary: ordinaryIWL => O.ValidOrdinaryApplication.createApp({...ordinaryApp, iwl: ordinaryIWL}),
        _: () => ordinaryApp,
      })(iwl),
    Undetermined: identity,
  });

export const changeHeartStroke = (heartStroke: HS.HeartStroke) =>
  matchApplicationByType<Application>({
    Group: groupApp =>
      HS.matchHeartStrokeByType({
        Group: groupHeartStroke => G.ValidGroupApplication.createApp({...groupApp, heartStroke: groupHeartStroke}),
        _: () => groupApp,
      })(heartStroke),
    NewYork: nyApp =>
      HS.matchHeartStrokeByType({
        NewYork: newYorkHeartStroke =>
          NY.ValidNewYorkApplication.createApp({...nyApp, heartStroke: newYorkHeartStroke}),
        _: () => nyApp,
      })(heartStroke),
    Ordinary: ordinaryApp =>
      HS.matchHeartStrokeByType({
        Ordinary: ordinaryHeartStroke =>
          O.ValidOrdinaryApplication.createApp({...ordinaryApp, heartStroke: ordinaryHeartStroke}),
        _: () => ordinaryApp,
      })(heartStroke),
    Undetermined: identity,
  });

export const changeAccident = (accident: A.Accident) =>
  matchApplicationByType<Application>({
    Group: groupApp =>
      A.matchAccidentByType({
        Group: groupAccident => G.ValidGroupApplication.createApp({...groupApp, accident: groupAccident}),
        _: () => groupApp,
      })(accident),
    NewYork: nyApp =>
      A.matchAccidentByType({
        NewYork: newYorkAccident => NY.ValidNewYorkApplication.createApp({...nyApp, accident: newYorkAccident}),
        _: () => nyApp,
      })(accident),
    Ordinary: ordinaryApp =>
      A.matchAccidentByType({
        Ordinary: ordinaryAccident =>
          O.ValidOrdinaryApplication.createApp({...ordinaryApp, accident: ordinaryAccident}),
        _: () => ordinaryApp,
      })(accident),
    Undetermined: identity,
  });

export const changePinCare = (pinCare: P.PinCare) =>
  matchApplicationByType<Application>({
    Group: groupApp =>
      P.matchPinCareByType({
        Group: groupPinCare => G.ValidGroupApplication.createApp({...groupApp, pinCare: groupPinCare}),
        _: () => groupApp,
      })(pinCare),
    NewYork: nyApp =>
      P.matchPinCareByType({
        NewYork: newYorkPinCare => NY.ValidNewYorkApplication.createApp({...nyApp, pinCare: newYorkPinCare}),
        _: () => nyApp,
      })(pinCare),
    Ordinary: ordinaryApp =>
      P.matchPinCareByType({
        Ordinary: ordinaryPinCare => O.ValidOrdinaryApplication.createApp({...ordinaryApp, pinCare: ordinaryPinCare}),
        _: () => ordinaryApp,
      })(pinCare),
    Undetermined: identity,
  });

export const changeUWQuestions = (uwQuestions: UWQuestions) =>
  matchApplicationByType<Application>({
    Group: groupApp => G.ValidGroupApplication.createApp({...groupApp, uwQuestions}),
    NewYork: nyApp => NY.ValidNewYorkApplication.createApp({...nyApp, uwQuestions}),
    Ordinary: ordinaryApp => O.ValidOrdinaryApplication.createApp({...ordinaryApp, uwQuestions}),
    Undetermined: identity,
  });

export const changeUniversalLife = (universalLife: UniversalLife) =>
  matchApplicationByType<Application>({
    Group: groupApp =>
      matchULByType({
        Group: groupUL => G.ValidGroupApplication.createApp({...groupApp, universalLife: groupUL}),
        _: () => groupApp,
      })(universalLife),
    NewYork: nyApp =>
      matchULByType({
        NewYork: newYorkUL => NY.ValidNewYorkApplication.createApp({...nyApp, universalLife: newYorkUL}),
        _: () => nyApp,
      })(universalLife),
    Ordinary: ordinaryApp =>
      matchULByType({
        Ordinary: ordinaryUL => O.ValidOrdinaryApplication.createApp({...ordinaryApp, universalLife: ordinaryUL}),
        _: () => ordinaryApp,
      })(universalLife),
    Undetermined: identity,
  });

export const changeGLTERM = (glterm: GLTERM) =>
  matchApplicationByType<Application>({
    Group: groupApp =>
      matchGLTERMByType({
        Group: groupGLTERM => G.ValidGroupApplication.createApp({...groupApp, glterm: groupGLTERM}),
        _: () => groupApp,
      })(glterm),
    NewYork: nyApp =>
      matchGLTERMByType({
        NewYork: newYorkGLTERM => NY.ValidNewYorkApplication.createApp({...nyApp, glterm: newYorkGLTERM}),
        _: () => nyApp,
      })(glterm),
    Ordinary: ordinaryApp =>
      matchGLTERMByType({
        Ordinary: ordinaryGLTERM => O.ValidOrdinaryApplication.createApp({...ordinaryApp, glterm: ordinaryGLTERM}),
        _: () => ordinaryApp,
      })(glterm),
    Undetermined: identity,
  });

export const changeAccidentNewYork = (accidentNewYork: ANY.AccidentNewYork) =>
  matchApplicationByType<Application>({
    Group: groupApp =>
      ANY.matchAccidentNewYorkByType({
        Group: groupAccidentNewYork =>
          G.ValidGroupApplication.createApp({...groupApp, accidentNewYork: groupAccidentNewYork}),
        _: () => groupApp,
      })(accidentNewYork),
    NewYork: nyApp =>
      ANY.matchAccidentNewYorkByType({
        NewYork: newYorkAccidentNewYork =>
          NY.ValidNewYorkApplication.createApp({...nyApp, accidentNewYork: newYorkAccidentNewYork}),
        _: () => nyApp,
      })(accidentNewYork),
    Ordinary: ordinaryApp =>
      ANY.matchAccidentNewYorkByType({
        Ordinary: ordinaryAccidentNewYork =>
          O.ValidOrdinaryApplication.createApp({...ordinaryApp, accidentNewYork: ordinaryAccidentNewYork}),
        _: () => ordinaryApp,
      })(accidentNewYork),
    Undetermined: identity,
  });

export const changeCriticalIllnessNY = (criticalIllnessNY: CriticalIllnessNY) =>
  matchApplicationByType<Application>({
    Group: groupApp =>
      matchCriticalIllnessNYByType({
        Group: groupCriticalIllnessNY =>
          G.ValidGroupApplication.createApp({...groupApp, criticalIllnessNY: groupCriticalIllnessNY}),
        _: () => groupApp,
      })(criticalIllnessNY),
    NewYork: nyApp =>
      matchCriticalIllnessNYByType({
        NewYork: newYorkCriticalIllnessNY =>
          NY.ValidNewYorkApplication.createApp({...nyApp, criticalIllnessNY: newYorkCriticalIllnessNY}),
        _: () => nyApp,
      })(criticalIllnessNY),
    Ordinary: ordinaryApp =>
      matchCriticalIllnessNYByType({
        Ordinary: ordinaryCriticalIllnessNY =>
          O.ValidOrdinaryApplication.createApp({...ordinaryApp, criticalIllnessNY: ordinaryCriticalIllnessNY}),
        _: () => ordinaryApp,
      })(criticalIllnessNY),
    Undetermined: identity,
  });

export const changeHippaTrustees = (hippaTrustees: HippaTrustees) =>
  matchApplicationByType<Application>({
    Group: groupApp => G.ValidGroupApplication.createApp({...groupApp, hippaTrustees}),
    NewYork: nyApp => NY.ValidNewYorkApplication.createApp({...nyApp, hippaTrustees}),
    Ordinary: ordinaryApp => O.ValidOrdinaryApplication.createApp({...ordinaryApp, hippaTrustees}),
    Undetermined: identity,
  });

export const changePremium = (premium: Premium) =>
  matchApplicationByType<Application>({
    Group: groupApp => G.ValidGroupApplication.createApp({...groupApp, premium}),
    NewYork: nyApp => NY.ValidNewYorkApplication.createApp({...nyApp, premium}),
    Ordinary: ordinaryApp => O.ValidOrdinaryApplication.createApp({...ordinaryApp, premium}),
    Undetermined: identity,
  });

export const changeAIP = (aip: AIP) =>
  matchApplicationByType<Application>({
    Group: groupApp =>
      matchAIPByType({
        Group: groupAIP => G.ValidGroupApplication.createApp({...groupApp, aip: groupAIP}),
        _: () => groupApp,
      })(aip),
    NewYork: nyApp =>
      matchAIPByType({
        NewYork: newYorkAIP => NY.ValidNewYorkApplication.createApp({...nyApp, aip: newYorkAIP}),
        _: () => nyApp,
      })(aip),
    Ordinary: ordinaryApp =>
      matchAIPByType({
        Ordinary: ordinaryAIP => O.ValidOrdinaryApplication.createApp({...ordinaryApp, aip: ordinaryAIP}),
        _: () => ordinaryApp,
      })(aip),
    Undetermined: identity,
  });

export const changePlanCode = (planCode: PlanCode) =>
  matchApplicationByType<Application>({
    Group: groupApp => G.ValidGroupApplication.createApp({...groupApp, planCode}),
    NewYork: nyApp => NY.ValidNewYorkApplication.createApp({...nyApp, planCode}),
    Ordinary: ordinaryApp => O.ValidOrdinaryApplication.createApp({...ordinaryApp, planCode}),
    Undetermined: identity,
  });

export const changePlanCodes = (planCodes: PlanCodes) =>
  matchApplicationByType<Application>({
    Group: groupApp => G.ValidGroupApplication.createApp({...groupApp, planCodes}),
    NewYork: nyApp => NY.ValidNewYorkApplication.createApp({...nyApp, planCodes}),
    Ordinary: ordinaryApp => O.ValidOrdinaryApplication.createApp({...ordinaryApp, planCodes}),
    Undetermined: identity,
  });

export const changeAdditionalProperties = (additionalProperties: AdditionalProperties) =>
  matchApplicationByType<Application>({
    Group: groupApp => G.ValidGroupApplication.createApp({...groupApp, additionalProperties}),
    NewYork: nyApp => NY.ValidNewYorkApplication.createApp({...nyApp, additionalProperties}),
    Ordinary: ordinaryApp => O.ValidOrdinaryApplication.createApp({...ordinaryApp, additionalProperties}),
    Undetermined: identity,
  });
